import React, { useState, useEffect, useCallback } from 'react';
import { useQuery } from 'react-query';
import {
	Grid,
	TextField,
	Typography,
	Box,
	InputAdornment,
	IconButton,
	Skeleton,
	Pagination,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	Chip,
	Paper,
	Slider,
	Button,
	Collapse,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import TalentCard from './TalentCard';
import { talentService } from '../../../services';
import { debounce } from 'lodash';
import { useSelector } from 'react-redux';
import { selectUser } from 'features/User/userSlice';

const ITEMS_PER_PAGE = 9;

const cities = ['Helsinki', 'Espoo', 'Tampere', 'Turku', 'Oulu', 'Jyväskylä'];
const skills = [
	'React',
	'JavaScript',
	'Python',
	'Java',
	'Node.js',
	'TypeScript',
];

const FilterBar = ({ filters, setFilters, handleVisibilityChange, isAdmin }) => (
	<Paper sx={{ p: 2, mb: 3 }}>
		<Grid container spacing={2}>
			<Grid item xs={12} md={4}>
				<FormControl fullWidth>
					<InputLabel>Kaupunki</InputLabel>
					<Select
						value={filters.city || ''}
						label="Kaupunki"
						onChange={(e) =>
							setFilters({ ...filters, city: e.target.value })
						}
					>
						<MenuItem value="">Kaikki</MenuItem>
						{cities.map((city) => (
							<MenuItem key={city} value={city}>
								{city}
							</MenuItem>
						))}
					</Select>
				</FormControl>
			</Grid>
			<Grid item xs={12} md={4}>
				<FormControl fullWidth>
					<InputLabel>Taidot</InputLabel>
					<Select
						multiple
						value={filters.skills || []}
						label="Taidot"
						onChange={(e) =>
							setFilters({ ...filters, skills: e.target.value })
						}
						renderValue={(selected) => (
							<Box
								sx={{
									display: 'flex',
									flexWrap: 'wrap',
									gap: 0.5,
								}}
							>
								{selected.map((value) => (
									<Chip key={value} label={value} />
								))}
							</Box>
						)}
					>
						{skills.map((skill) => (
							<MenuItem key={skill} value={skill}>
								{skill}
							</MenuItem>
						))}
					</Select>
				</FormControl>
			</Grid>
			<Grid item xs={12} md={4}>
				<FormControl fullWidth>
					<InputLabel>Järjestys</InputLabel>
					<Select
						value={filters.sortBy || 'name:asc'}
						label="Järjestys"
						onChange={(e) =>
							setFilters({ ...filters, sortBy: e.target.value })
						}
					>
						<MenuItem value="name:asc">Nimi (A-Ö)</MenuItem>
						<MenuItem value="name:desc">Nimi (Ö-A)</MenuItem>
						<MenuItem value="hourlyRate:asc">
							Tuntihinta (Pienin ensin)
						</MenuItem>
						<MenuItem value="hourlyRate:desc">
							Tuntihinta (Suurin ensin)
						</MenuItem>
					</Select>
				</FormControl>
			</Grid>
			{isAdmin && (
				<Grid item xs={12} md={4}>
					<FormControl fullWidth>
						<InputLabel>Näkyvyys</InputLabel>
						<Select
							value={filters.visibilityFilter}
							label="Näkyvyys"
							onChange={handleVisibilityChange}
						>
							<MenuItem value="all">Kaikki talentit</MenuItem>
							<MenuItem value="visible">Vain näkyvät</MenuItem>
							<MenuItem value="hidden">Vain piilotetut</MenuItem>
						</Select>
					</FormControl>
				</Grid>
			)}
			<Grid item xs={12}>
				<Typography gutterBottom>Tuntihinta (€/h)</Typography>
				<Slider
					value={[
						filters.minHourlyRate || 0,
						filters.maxHourlyRate || 100,
					]}
					onChange={(e, newValue) => {
						setFilters({
							...filters,
							minHourlyRate: newValue[0],
							maxHourlyRate: newValue[1],
						});
					}}
					onChangeCommitted={(e, newValue) => {
						setFilters({
							...filters,
							minHourlyRate: newValue[0],
							maxHourlyRate: newValue[1],
						});
					}}
					valueLabelDisplay="auto"
					min={0}
					max={100}
				/>
			</Grid>
		</Grid>
	</Paper>
);

const SearchInput = ({ searchTerm, onChange }) => (
	<Box sx={{ flexGrow: 1 }}>
		<TextField
			fullWidth
			placeholder="Etsi talentteja nimellä, kuvauksella tai taidoilla"
			variant="outlined"
			onChange={onChange}
			value={searchTerm}
			InputProps={{
				endAdornment: (
					<InputAdornment position="end">
						<SearchIcon />
					</InputAdornment>
				),
			}}
		/>
	</Box>
);

const TalentBrowser = () => {
	const [searchTerm, setSearchTerm] = useState('');
	const [debouncedTerm, setDebouncedTerm] = useState('');
	const [showFilters, setShowFilters] = useState(false);
	const [filters, setFilters] = useState({
		city: '',
		skills: [],
		sortBy: 'createdAt:desc',
		minHourlyRate: 0,
		maxHourlyRate: 100,
		visibilityFilter: 'visible',
		isHidden: false,
	});
	const [page, setPage] = useState(1);

	const user = useSelector(selectUser);
	const isCompany = user?.role === 'company';
	const isAdmin = user?.role === 'admin';

	// Force company users to only see visible talents
	useEffect(() => {
		if (isCompany) {
			setFilters(prev => ({
				...prev,
				visibilityFilter: 'visible',
				isHidden: false
			}));
		}
	}, [isCompany]);

	// Päivitetään debounced arvo viiveellä
	useEffect(() => {
		const timer = setTimeout(() => {
			setDebouncedTerm(searchTerm);
			setPage(1);
		}, 300);

		return () => clearTimeout(timer);
	}, [searchTerm]);

	const handleSearchChange = (e) => {
		setSearchTerm(e.target.value); // Tämä päivittyy heti
	};

	// Handle visibility filter change
	const handleVisibilityChange = (e) => {
		const newVisibilityFilter = e.target.value;
		const newIsHidden = newVisibilityFilter === 'hidden' ? true : 
						    newVisibilityFilter === 'visible' ? false : null;
		
		setFilters({ 
			...filters, 
			visibilityFilter: newVisibilityFilter,
			isHidden: newIsHidden
		});
		
		// Reset to page 1 when changing filters
		setPage(1);
	};

	const removeEmptyValues = (obj) => {
		const result = {};
		Object.entries(obj).forEach(([key, value]) => {
			if (value !== '' && value !== null && value !== undefined) {
				if (Array.isArray(value) && value.length === 0) return;
				if (key === 'minHourlyRate' && value === 0) return;
				if (key === 'maxHourlyRate' && value === 100) return;
				if (key === 'visibilityFilter') return; // Skip this key as we use isHidden instead
				result[key] = value;
			}
		});
		return result;
	};

	const { data, error, isLoading } = useQuery(
		[
			'talents',
			debouncedTerm,
			filters.isHidden,
			showFilters ? filters : { sortBy: 'name:asc' },
			page,
		],
		() => {
			const baseParams = {
				page,
				limit: ITEMS_PER_PAGE,
				sortBy: 'name:asc',
			};

			// For company users, always set isHidden to false
			if (isCompany) {
				baseParams.isHidden = false;
			} 
			// For admin users, use the filter setting
			else if (filters.isHidden !== null) {
				baseParams.isHidden = filters.isHidden;
			}

			if (debouncedTerm) {
				baseParams.searchText = debouncedTerm;
			}

			if (showFilters) {
				const activeFilters = removeEmptyValues(filters);
				Object.assign(baseParams, activeFilters);
			}

			return talentService.getTalents(baseParams);
		},
		{
			keepPreviousData: true,
		},
	);

	const talents = data?.data?.results || [];
	const totalPages = data?.data?.totalPages || 1;

	const handlePageChange = (event, newPage) => {
		setPage(newPage);
	};

	if (error)
		return <Typography color="error">Virhe ladattaessa dataa</Typography>;

	return (
		<div>
			<Box
				sx={{
					display: 'flex',
					mb: 2,
					gap: 2,
					flexDirection: { xs: 'column', md: 'row' },
				}}
			>
				<SearchInput
					searchTerm={searchTerm}
					onChange={handleSearchChange}
				/>
				{isAdmin && (
					<FormControl sx={{ minWidth: 200 }}>
						<InputLabel>Näkyvyys</InputLabel>
						<Select
							value={filters.visibilityFilter}
							label="Näkyvyys"
							onChange={handleVisibilityChange}
						>
							<MenuItem value="all">Kaikki talentit</MenuItem>
							<MenuItem value="visible">Vain näkyvät</MenuItem>
							<MenuItem value="hidden">Vain piilotetut</MenuItem>
						</Select>
					</FormControl>
				)}
				<Button
					variant="outlined"
					onClick={() => setShowFilters(!showFilters)}
					startIcon={<FilterListIcon />}
				>
					{showFilters ? 'Piilota suodattimet' : 'Näytä suodattimet'}
				</Button>
			</Box>

			<Collapse in={showFilters}>
				<FilterBar 
					filters={filters} 
					setFilters={setFilters} 
					handleVisibilityChange={handleVisibilityChange}
					isAdmin={isAdmin}
				/>
			</Collapse>

			{isLoading ? (
				<Grid container spacing={4}>
					{Array.from(new Array(ITEMS_PER_PAGE)).map((_, index) => (
						<Grid item xs={12} md={6} lg={4} key={index}>
							<Skeleton
								variant="rectangular"
								height={300}
								width="100%"
							/>
						</Grid>
					))}
				</Grid>
			) : (
				<>
					{talents.length === 0 ? (
						<Box sx={{ textAlign: 'center', py: 4 }}>
							<Typography variant="h6">
								Ei tuloksia. Kokeile eri hakuehtoja.
							</Typography>
						</Box>
					) : (
						<>
							<Grid container spacing={4}>
								{talents.map((talent) => (
									<Grid
										item
										xs={12}
										md={6}
										lg={4}
										key={talent.id}
									>
										<TalentCard 
											talent={talent} 
											onAddToTeam={() => {}}
											isInTeam={false}
										/>
									</Grid>
								))}
							</Grid>
							<Box
								sx={{
									display: 'flex',
									justifyContent: 'center',
									pt: 4,
								}}
							>
								<Pagination
									count={totalPages}
									page={page}
									onChange={handlePageChange}
									color="primary"
								/>
							</Box>
						</>
					)}
				</>
			)}
		</div>
	);
};

export default TalentBrowser;
